import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { IS_MAIN_COMPANY, itemCountPerPage } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { deleteExternalTicket, deleteInternalTicket, getConfig, getTickets } from 'services/apis/ticket'
import { svgSearch } from 'stories/assets'
import { Button, ButtonGroup, Input2, Select2 } from 'stories/components'
import { confirm } from 'utils'
import { useTitle } from 'utils/pageTitle'

import { ExternalTicketsTable } from './ExternalTicketsTable'
import { InternalTicketsTable } from './InternalTicketsTable'
import { type ITicket, TicketStatusText } from './types'

export const ManageTickets = () => {
  useTitle(`Manage Tickets`)

  const navigate = useHistory()
  const auth = useSelector((state: any) => state.auth)
  const [isLoading, setLoading] = useState(false)
  const [pageNum, setPageNum] = useState(0)
  const [total, setTotal] = useState(0)
  const [values, setValues] = useState<any[]>([])
  const [offsetToken, setOffsetToken] = useState<string | undefined>(undefined)
  const [filterQuery, setFilterQuery] = useState('')
  const [customFieldKeys, setCustomFieldKeys] = useState<string[]>([])
  const [enableSubmitButton, setEnableSubmitButton] = useState(true)

  const [filters, setFilters] = useState<Record<string, any>>({
    query: '',
    orderBy: 'created_at',
    orderDir: -1,
    status: '',
    type: 'External',
  })
  const [isGetUsersOnce, setIsGetUsersOnce] = useState(false)
  const { hasPermission } = usePermissions()

  useEffect(() => {
    if (IS_MAIN_COMPANY || !auth.isAuthenticated || !hasPermission('AdminTools')) {
      navigate.push('/')
      return
    }
    getConfig().then((data: any) => {
      const customKeys = Object.keys(data || {})
      setCustomFieldKeys(customKeys)
    })
  }, [])

  useEffect(() => {
    if (isLoading) return
    filterData(filters).then(() => {
      setIsGetUsersOnce(true)
    })
  }, [pageNum])

  useEffect(() => {
    if (!isGetUsersOnce) return
    const timeOutId = setTimeout(() => !isLoading && (filterData(filters, 0), setPageNum(0)), 700)
    return () => clearTimeout(timeOutId)
  }, [filterQuery])

  const onChangeFilter = (key: string, value: any) => {
    if (!isGetUsersOnce) return
    const newFilters = cloneDeep(filters)
    newFilters[key] = value
    setFilters(newFilters)
    if (key === 'query') setFilterQuery(value)
    else {
      filterData(newFilters, 0)
      setPageNum(0)
    }
  }

  const sortData = (data: any[], orderBy: string, orderDir: number) => {
    return [...data].sort((a, b) => {
      let aValue = a[orderBy]
      let bValue = b[orderBy]

      if (orderBy === 'stageNumber' || orderBy === 'stage') {
        aValue = a.stageNumber
        bValue = b.stageNumber
      } else {
        if (aValue == null) {
          const customField = a.custom_fields?.find((field: any) => field.name === orderBy)
          aValue = customField?.display_value
        }
        if (bValue == null) {
          const customField = b.custom_fields?.find((field: any) => field.name === orderBy)
          bValue = customField?.display_value
        }
      }

      if (aValue == null) return 1
      if (bValue == null) return -1

      if (aValue < bValue) return orderDir === 1 ? -1 : 1
      if (aValue > bValue) return orderDir === 1 ? 1 : -1
      return 0
    })
  }

  const filterData = async (filters: any, _pageNum: number = -1, appendData: boolean = false) => {
    if (isLoading) return
    if (_pageNum === -1) _pageNum = pageNum
    const filterData = {
      ...filters,
      skip: _pageNum * itemCountPerPage,
      count: itemCountPerPage,
    }
    if (filters.status === '') {
      delete filterData.status
    }
    setLoading(true)
    try {
      const { data, total, offset, success } = await getTickets(filterData)

      if (success === false) {
        setEnableSubmitButton(false)
      }

      if (filters.type === 'Internal') {
        setTotal(total)
        setValues(data)
      } else {
        if (appendData) {
          setValues((prevValues) => {
            const combined = [...prevValues, ...data]
            return sortData(combined, filters.orderBy, filters.orderDir)
          })
        } else {
          setValues(sortData(data, filters.orderBy, filters.orderDir))
        }
      }
      setOffsetToken(offset)
    } catch (error) {
      console.error('Failed to fetch tickets:', error)
    } finally {
      setLoading(false)
    }
  }

  const onPageNavigate = (num: number) => {
    setPageNum(num)
  }

  const onSort = (key: string, sortOrder: number) => {
    if (sortOrder == 0) sortOrder = -1
    const newFilters = cloneDeep(filters)
    newFilters['orderBy'] = key
    newFilters['orderDir'] = sortOrder
    newFilters['isSorting'] = true
    setFilters(newFilters)

    filterData(newFilters)
  }

  const onLoadMore = () => {
    if (offsetToken) {
      const newFilters = cloneDeep(filters)
      newFilters['offset'] = offsetToken
      newFilters['isSorting'] = false
      setFilters(newFilters)

      filterData(newFilters, -1, true)
    }
  }

  const goToMessages = (value: any) => {
    navigate.push(`/tickets/external/${value.gid}`)
  }

  const onAdd = () => {
    navigate.push('/create-ticket')
  }

  const onDeleteInternalTicket = async (ticket: ITicket) => {
    const content = (
      <div className="mb-4">
        <p className="text-gray-800 font-semibold mb-2">Are you sure you want to delete this ticket?</p>
        <p className="text-gray-600 text-sm">Subject: {ticket.title}</p>
      </div>
    )
    const isConfirmed = await confirm(content)

    if (isConfirmed) {
      setLoading(true)
      try {
        await deleteInternalTicket(ticket.id)
        await filterData(filters)
      } catch (error) {
        console.error('Failed to delete ticket:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  const onDeleteExternalTicket = async (ticket: any) => {
    const content = (
      <div className="mb-4">
        <p className="text-gray-800 font-semibold mb-2">Are you sure you want to delete this ticket?</p>
        <p className="text-gray-600 text-sm">Subject: {ticket.name}</p>
      </div>
    )
    const isConfirmed = await confirm(content)

    if (isConfirmed) {
      setLoading(true)
      try {
        await deleteExternalTicket(ticket.gid)
        setValues((prevValues) => prevValues.filter((item) => item.gid !== ticket.gid))
      } catch (error) {
        console.error('Failed to delete ticket:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <div className="py-6 px-2">
      <div className="relative shadow1 max-w-screen-2xl m-auto bg-white rounded p-3 md:p-7 pb-3 md:pb-3 sm:text-center lg:text-left w-full">
        <LayoutLoading show={isLoading} />

        <div className="gap-2 mb-3 flex items-center justify-between">
          <h1 className="text-2xl font-variation-settings-600">Tickets</h1>

          <Button onClick={onAdd} disabled={!enableSubmitButton}>
            Submit a Ticket
          </Button>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3 gap-4">
          <div className="mb-3 hidden">
            <ButtonGroup
              title={['External', 'Internal']}
              value={filters.type}
              onChange={(value) => {
                if (filters.type !== value) {
                  setValues([])
                  const newFilters = cloneDeep(filters)
                  newFilters.type = value
                  delete newFilters.offset
                  setFilters(newFilters)
                  filterData(newFilters, 0)
                  setPageNum(0)
                }
              }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Input2
              type="search"
              title="Search ..."
              hasIcon
              icon={svgSearch}
              value={filters.query}
              onChange={(value) => onChangeFilter('query', value)}
              className="w-120"
            />
            {filters.type === 'Internal' && (
              <Select2
                id="status"
                title="Status"
                options={TicketStatusText}
                value={filters.status}
                className="capitalize w-64"
                hasDefaultOption
                defaultOptionText=""
                onChange={(value) => onChangeFilter('status', value)}
              />
            )}
          </div>
        </div>

        {filters.type === 'Internal' ? (
          <InternalTicketsTable
            values={values}
            pageNum={pageNum}
            filters={filters}
            onSort={onSort}
            goToMessages={goToMessages}
            onDelete={onDeleteInternalTicket}
            total={total}
            onPageNavigate={onPageNavigate}
          />
        ) : (
          <ExternalTicketsTable
            values={values}
            filters={filters}
            customFieldKeys={customFieldKeys}
            onSort={onSort}
            goToMessages={goToMessages}
            onDelete={onDeleteExternalTicket}
            onLoadMore={onLoadMore}
            offset={offsetToken}
          />
        )}
      </div>
    </div>
  )
}
